"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.generateReadableId = generateReadableId;
exports.generateReadableTableColumnId = generateReadableTableColumnId;
exports.getFieldPositionsCount = getFieldPositionsCount;
exports.getFieldsCount = getFieldsCount;
exports.getViewsFieldPositionsCount = getViewsFieldPositionsCount;
/**
* Counts fields of a specific type in an array of fields
*
* @param {Array} fields - Array of fields that have a .type property
* @param {String} type - The field type to count
* @returns {Number} - Count of fields matching the type
*/
function countFieldsByType(fields, type) {
  if (!fields || !Array.isArray(fields)) return 0;
  return fields.filter(function (field) {
    return field && field.type === type;
  }).length;
}

/**
* Counts field positions of a specific type in document fields
*
* @param {Array} fields - Array of fields
* @param {String} type - Field type to count
* @returns {Number} - Count of fields matching the type
*/
function getFieldsCount(fields, type) {
  if (!fields || !Array.isArray(fields)) {
    console.warn('getFieldsCount: fields parameter is missing or not an array');
    return 0;
  }
  return countFieldsByType(fields, type);
}

/**
* Counts field positions of a specific type in an array of pages
*
* @param {Array} pages - Array of pages with fieldPositions
* @param {String} type - Field type to count
* @returns {Number} - Count of fieldPositions matching the type
*/
function getFieldPositionsCount(pages, type) {
  if (!pages || !Array.isArray(pages)) {
    return 0;
  }
  if (!pages.length) return 0;
  var count = 0;
  pages.forEach(function (page) {
    if (page && page.fieldPositions) {
      count += countFieldsByType(page.fieldPositions, type);
    }
  });
  return count;
}

/**
* Counts field positions in views
*
* @param {Array} views - Array of views with pages with fieldPositions
* @param {String} type - Field type to count
* @returns {Number} - Count of fieldPositions in views matching the type
*/
function getViewsFieldPositionsCount(views, type) {
  if (!views || !Array.isArray(views)) {
    return 0;
  }
  if (!views.length) return 0;
  var count = 0;
  views.forEach(function (view) {
    if (view && view.pages && view.pages.length) {
      count += getFieldPositionsCount(view.pages, type);
    }
  });
  return count;
}

/**
* Gets the next field ID for a specific type by counting existing fields
* and incrementing. It is built to support any combination of the parameters.
*
* But it is recommended to use it with all 3 parts if possible.
*
* @param {Object} parts - Object containing fields, pages, and views
* @param {Array} parts.fields - Array of fields
* @param {Array} parts.pages - Array of pages with fieldPositions
* @param {Array} parts.views - Array of views with pages
* @param {String} type - The field type to count
* @param {String} prefix - An optional prefix to add to the ID
* @returns {String} - An ID in the format "prefix{type}{count+1}" (e.g. "text3")
*/
function generateReadableId(parts, type) {
  var splitterId = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
  var fields = parts === null || parts === void 0 ? void 0 : parts.fields;
  var pages = parts === null || parts === void 0 ? void 0 : parts.pages;
  var views = parts === null || parts === void 0 ? void 0 : parts.views;
  if (!type) throw new Error('generateReadableId: type is required');

  // Check if all three parameters are missing
  if (!fields && !pages && !views) {
    throw new Error('generateReadableId: at least one of fields, pages, or views is required');
  }
  if (!fields) console.warn('generateReadableId: fields parameter is missing');

  // Note: leaving for now until we allow targeting fieldPositions
  // if (pages) count += getFieldPositionsCount(pages, type);
  // if (views && views.length > 1) count += getViewsFieldPositionsCount(views, type);

  var count = fields.length ? getFieldsCount(fields, type) : 0;
  var readableId = "".concat(type).concat(splitterId).concat(count + 1);
  while (hasDuplicateId(fields, readableId)) {
    count++;
    readableId = "".concat(type).concat(splitterId).concat(count + 1);
  }
  return readableId;
}
function hasDuplicateId(items, targetId) {
  return items.some(function (item) {
    return item._id === targetId;
  });
}

/**
* Generates a readable table column ID by counting existing columns
*
* @param {String} tableId - The ID of the table (used as prefix)
* @param {Array} tableColumns - Array of table column objects
* @returns {String} - An ID in the format "{tableId}Column{count+1}" (e.g. "table1Column1")
*/
function generateReadableTableColumnId(tableId, tableColumns, columnId) {
  //Add logic that ensures that created Id is not a duplicate
  if (!tableId) throw new Error('generateReadableTableColumnId: tableId is required');
  if (!columnId) throw new Error('generateReadableTableColumnId: columnId is required');
  if (!tableColumns || !Array.isArray(tableColumns)) {
    console.warn('generateReadableTableColumnId: tableColumns parameter is missing or not an array');
    return "".concat(tableId, "Column1");
  }
  var counts = {};
  var generatedId = null;

  //Filter out undefined and null values from tableColumns
  var validColumns = tableColumns.filter(function (column) {
    return column !== undefined && column !== null;
  });
  validColumns.forEach(function (col) {
    var type = (col.type || 'unknown').toLowerCase();
    counts[type] = (counts[type] || 0) + 1;
    var readableId = "".concat(type).concat(counts[type]);
    if (col._id === columnId) {
      /**
       * If the generatedReadableId is already present in the tableColumns,
        * we need to increment the count until we find a unique ID.
        * This ensures that the new column ID is unique.
        */
      while (hasDuplicateId(validColumns, readableId)) {
        counts[type]++;
        readableId = "".concat(type).concat(counts[type]);
      }
      generatedId = readableId;
    }
  });
  return generatedId;
}